import React, { useState } from 'react';
import { LayoutChangeEvent, LayoutRectangle, Pressable, StyleProp, View, ViewStyle } from 'react-native';
import { colors, shadows, zIndex } from '../../utils/theme';
import { FixedContentContainer } from './FixedPositionPortalContainer';
import { useElementPosition } from '../../hooks/useElementPosition';
import { useIsDisabledByParent } from './IsDisabledProvider';

type PopoverPosition = 'bottom-right-align' | 'top-right-align' | 'top-left-align';

type RenderButtonArgs = {
    ref: any;
    onPress: () => void;
    onLayout: (e: LayoutChangeEvent) => void;
    isDisabled: boolean;
};

export type PopoverOwnProps = {
    renderButton: (args: RenderButtonArgs) => React.ReactNode;
    children: (togglePopover: () => void) => React.ReactNode | React.ReactNode[];
    popoverPosition: PopoverPosition;
    popoverWidth?: number;
};

export type PopoverProps = PopoverOwnProps;

const Popover: React.FC<PopoverProps> = (props) => {
    const isDisabledByParent = useIsDisabledByParent();
    const buttonPosition = useElementPosition({});
    const [popoverVisible, setPopoverVisible] = useState(false);
    const [buttonLayout, setButtonLayout] = useState<LayoutRectangle | null>(null);

    const togglePopover = () => {
        setPopoverVisible(!popoverVisible);
    };

    const getPopoverStyle = (): StyleProp<ViewStyle> => {
        if (!buttonPosition.position) return {};

        const popoverWidth = props.popoverWidth || 240;

        const bottom =
            props.popoverPosition === 'bottom-right-align'
                ? buttonPosition.position.getBottomAlignFromTopWithPadding(8)
                : undefined;
        const top =
            props.popoverPosition === 'top-right-align' || props.popoverPosition === 'top-left-align'
                ? buttonPosition.position.getTopAlignFromBottomWithPadding(8)
                : undefined;

        const right =
            props.popoverPosition === 'top-right-align' || props.popoverPosition === 'bottom-right-align'
                ? buttonPosition.position.right
                : undefined;

        const left = props.popoverPosition === 'top-left-align' ? buttonPosition.position.left : undefined;

        const style = {
            position: 'absolute' as const,
            right,
            bottom: bottom,
            top: top,
            left,
            width: popoverWidth,
            backgroundColor: colors.White,
            borderRadius: 5,
            borderWidth: 1,
            borderColor: colors['Gray-Text'],
            zIndex: zIndex.prompt,
            ...shadows.default,
        };

        return style;
    };

    return (
        <>
            {props.renderButton({
                ref: buttonPosition.ref,
                onPress: () => {
                    if (buttonLayout) {
                        togglePopover();
                    } else {
                        // give button a chance to register position
                        setTimeout(() => {
                            setPopoverVisible(true);
                        }, 300);
                    }
                },
                onLayout: (e) => {
                    setButtonLayout(e.nativeEvent.layout);
                    buttonPosition.onLayout(e);
                },
                isDisabled: isDisabledByParent || !buttonPosition.ref,
            })}
            {popoverVisible ? (
                <FixedContentContainer>
                    <Pressable
                        onPress={() => setPopoverVisible(false)}
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            overflow: 'hidden',
                        }}
                    >
                        <View style={getPopoverStyle()} testID="Popover-popover">
                            {props.children(togglePopover)}
                        </View>
                    </Pressable>
                </FixedContentContainer>
            ) : null}
        </>
    );
};

export default React.memo(Popover);
